const MAX_TEXT = 1000;
const ALLOWED_LEVELS = new Set(['debug', 'info', 'warning', 'error']);
const SLOW_API_THRESHOLD_MS = 1200;
const FLUSH_DELAY_MS = 30000;
const MAX_QUEUE_SIZE = 100;
const MAX_BATCH_SIZE = 100;

function text(value, max = MAX_TEXT) {
  return String(value ?? '').slice(0, max);
}

// safePayload() redacts by object KEY, which cannot see inside a URL string. Recording
// location.href verbatim therefore bypasses redaction entirely: the moment any screen puts
// an identifier or a one-time token in a query parameter, it lands in the telemetry log in
// clear text. Only the origin+path carries diagnostic value, so drop the query and hash.
function safeUrl(href) {
  const raw = String(href ?? '');
  if (raw === '') return '';
  try {
    const url = new URL(raw, globalThis.location?.origin || 'http://localhost');
    return text(url.origin + url.pathname, 500);
  } catch {
    return text(raw.split('?')[0].split('#')[0], 500);
  }
}

function safePayload(value, depth = 0) {
  if (depth > 4) return '[depth-limited]';
  if (value === null || ['boolean', 'number'].includes(typeof value)) return value;
  if (typeof value === 'string') return text(value, 2000);
  if (Array.isArray(value)) return value.slice(0, 30).map((item) => safePayload(item, depth + 1));
  if (typeof value !== 'object') return text(value);
  const result = {};
  for (const [key, item] of Object.entries(value).slice(0, 40)) {
    if (/password|secret|token|csrf|authorization|cookie/i.test(key)) result[key] = '[redacted]';
    else result[key] = safePayload(item, depth + 1);
  }
  return result;
}

export class TelemetryClient {
  constructor({ endpoint = 'api.php', csrfToken = () => '', fetchImpl = globalThis.fetch?.bind(globalThis) } = {}) {
    this.endpoint = endpoint; this.csrfToken = csrfToken; this.fetchImpl = fetchImpl; this.installed = false; this.enabled = false; this.queue = []; this.flushTimer = null; this.flushing = false;
    this.boundError = (event) => this.record('browser.error', {
      level: 'error', message: event.message || 'Browser error',
      payload: { filename: event.filename || '', line: event.lineno || 0, column: event.colno || 0 },
    });
    this.boundRejection = (event) => this.record('browser.unhandled_rejection', {
      level: 'error', message: event.reason?.message || String(event.reason || 'Unhandled rejection'),
    });
    this.boundPageHide = () => this.flush(true);
  }

  install() {
    if (this.installed) return;
    this.installed = true;
    globalThis.addEventListener('error', this.boundError);
    globalThis.addEventListener('unhandledrejection', this.boundRejection);
    globalThis.addEventListener('pagehide', this.boundPageHide);
  }

  setEnabled(enabled) {
    this.enabled = Boolean(enabled);
    if (this.enabled) return;
    clearTimeout(this.flushTimer); this.flushTimer = null; this.queue = [];
  }

  destroy() {
    if (!this.installed) return;
    globalThis.removeEventListener('error', this.boundError);
    globalThis.removeEventListener('unhandledrejection', this.boundRejection);
    globalThis.removeEventListener('pagehide', this.boundPageHide);
    clearTimeout(this.flushTimer); this.flushTimer = null; this.flush(true); this.installed = false;
  }

  apiResult(result) {
    if (!this.enabled || !result || result.aborted) return;
    const action = String(result.action || '');
    if (!action || action.startsWith('telemetry.')) return;
    if (result.ok && action.startsWith('sync.')) return;
    const duration = Number(result.durationMs || 0);
    if (result.ok && duration < SLOW_API_THRESHOLD_MS) return;
    // A browser that never reached the server (fetch() threw -> network_error, status 0) is a
    // client-side connectivity blip, not a server runtime error. error-policy.js already grades
    // network_error as 'warning'; keep this recording path consistent so a single Wi-Fi/sleep/
    // deploy-restart hiccup does not trip the runtime-error health check.
    const transportUnreachable = !result.ok
      && (result.errorCode === 'network_error' || Number(result.status || 0) === 0);
    const level = result.ok || transportUnreachable ? 'warning' : 'error';
    this.record('api.request.completed', {
      level, action,
      duration_ms: duration, status_code: result.status,
      message: result.ok ? 'slow_request' : result.errorCode || 'request_failed', payload: {},
    });
  }

  navigation(route) {
    let enabled = false;
    try { enabled = globalThis.localStorage?.getItem('shipsops.telemetry.navigation') === '1'; } catch (_) {}
    if (enabled) this.record('ui.navigation', { action: 'navigate', payload: safePayload(route) });
  }

  record(eventType, options = {}) {
    if (!this.enabled) return;
    if (this.queue.length >= MAX_QUEUE_SIZE) this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE + 1);
    this.queue.push({
      event_type: text(eventType, 100), level: ALLOWED_LEVELS.has(options.level) ? options.level : 'info',
      event_action: text(options.action || '', 100),
      duration_ms: Number.isFinite(options.duration_ms) ? Math.max(0, Math.round(options.duration_ms)) : null,
      status_code: Number.isFinite(options.status_code) ? Math.max(0, Math.round(options.status_code)) : null,
      message: text(options.message || '', 10000), url: safeUrl(globalThis.location?.href || ''),
      payload: safePayload(options.payload || {}), occurred_at: new Date().toISOString(),
    });
    if (this.queue.length >= MAX_BATCH_SIZE) this.flush(); else this.scheduleFlush();
  }

  scheduleFlush() {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => { this.flushTimer = null; this.flush(); }, FLUSH_DELAY_MS);
  }

  async flush(keepalive = false) {
    if (!this.enabled || this.flushing || this.queue.length === 0) return;
    clearTimeout(this.flushTimer); this.flushTimer = null;
    const events = this.queue.splice(0, MAX_BATCH_SIZE); this.flushing = true;
    const token = String(this.csrfToken?.() || '');
    try {
      if (typeof this.fetchImpl !== 'function') throw new Error('Telemetry transport is unavailable');
      const response = await this.fetchImpl(this.endpoint, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', keepalive,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: 'telemetry.events.record', _csrf: token, events }),
      });
      if (!response?.ok) throw new Error(`Telemetry HTTP ${Number(response?.status || 0)}`);
    } catch {
      if (this.enabled) this.queue = [...events, ...this.queue].slice(0, MAX_QUEUE_SIZE);
    } finally {
      this.flushing = false;
      if (this.queue.length) this.scheduleFlush();
    }
  }
}
